import dayjs from 'dayjs';
import { CacheConfig, CacheEntry, CacheStoreConfig } from '../types/cache';
import { DB_VERSION } from '@app/constants/indexed-db';

function openDb(dbName: string, storeName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getCache<T>(
  key: string,
  storeConfig: CacheStoreConfig,
): Promise<CacheEntry<T> | undefined> {
  const db = await openDb(storeConfig.dbName, storeConfig.storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeConfig.storeName, 'readonly');
    const store = tx.objectStore(storeConfig.storeName);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as CacheEntry<T> | undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function setCache<T>(
  key: string,
  data: T,
  storeConfig: CacheStoreConfig,
): Promise<void> {
  const db = await openDb(storeConfig.dbName, storeConfig.storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeConfig.storeName, 'readwrite');
    const store = tx.objectStore(storeConfig.storeName);
    const entry: CacheEntry<T> = {
      data,
      timestamp: dayjs().unix(),
    };
    const req = store.put(entry, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function clearCacheEntry(key: string, storeConfig: CacheStoreConfig): Promise<void> {
  const db = await openDb(storeConfig.dbName, storeConfig.storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeConfig.storeName, 'readwrite');
    const store = tx.objectStore(storeConfig.storeName);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function clearCacheStore(storeConfig: CacheStoreConfig): Promise<void> {
  const db = await openDb(storeConfig.dbName, storeConfig.storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeConfig.storeName, 'readwrite');
    const store = tx.objectStore(storeConfig.storeName);
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function isCacheValid<T>(cache: CacheEntry<T>, config: CacheConfig): boolean {
  if (typeof cache?.timestamp !== 'number' || typeof config?.ttlSeconds !== 'number') return false;
  return dayjs().unix() - cache.timestamp < config.ttlSeconds;
}